import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Compass, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/utils/authContext";
import DynamicCampaign from "./dynamicCampaign";

interface HeroBannerProps {
  isExploreEnabled: boolean;
  isLoginEnabled: boolean;
  contentData: any[];
  configData: any[];
  base_path: string;
}

export default function HeroBanner({
  isExploreEnabled,
  isLoginEnabled,
  contentData,
  configData,
  base_path,
}: HeroBannerProps) {
  const navigate = useNavigate();

  const { isLoggedIn } = useAuth();
  const [currentHeadlineIndex, setCurrentHeadlineIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [backgroundImage, setBackgroundImage] = useState<string>("");

  // Dynamically map headlines and subheadlines from contentData
  const getDynamicHeadlinesAndSubheadlines = () => {
    const heroContent = contentData.find((page: any) => page.name === "Landing Page");
    const contentFields = heroContent?.fields || [];
    if (!contentFields || contentFields.length === 0) return { headlines: [], subheadlines: [] };

    const landingPageField = contentFields[1];
    const panelComponents = (landingPageField && 'components' in landingPageField)
      ? (landingPageField as { components?: any[] }).components || []
      : [];

    const landingPagePanel = panelComponents.find((comp: any) => comp.type === "panel" && comp.title?.includes("Landing Page Settings"));
    const landingPageComponents = landingPagePanel?.components || [];
    const headlinesField = landingPageComponents.find((field: any) => field.key === "headlines");
    if (headlinesField && Array.isArray(headlinesField.defaultValue)) {
      const headlines = headlinesField.defaultValue.map((item: any) => item.headline).filter(Boolean);
      const subheadlines = headlinesField.defaultValue.map((item: any) => item.subheadline).filter(Boolean);
      return { headlines, subheadlines };
    }
    return { headlines: [], subheadlines: [] };
  };

  const [dynamicHeadlines, setDynamicHeadlines] = useState<string[]>([]);
  const [dynamicSubheadlines, setDynamicSubheadlines] = useState<string[]>([]);

  useEffect(() => {
    const { headlines, subheadlines } = getDynamicHeadlinesAndSubheadlines();
    setDynamicHeadlines(headlines);
    setDynamicSubheadlines(subheadlines);
  }, [contentData]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentHeadlineIndex((prev) => (prev + 1) % dynamicHeadlines.length);
        setIsVisible(true);
      }, 300); // Half of transition time for smooth effect
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [dynamicHeadlines.length]);

  // Explore button navigation logic
  const handleExplore = () => {
    localStorage.setItem('clearStartCampaignFilters', 'true');
    if (isLoggedIn) {
      navigate(`${base_path}/propertylist`);
    } else {
      navigate(`${base_path}/explore`);
    }
  };

  // Register button navigation
  const handleRegister = () => {
    navigate(`${base_path}/sign-up`);
  };

  // Function to get background image from the Backgroundimage editgrid
  const getBackgroundImage = () => {
    const heroContent = contentData.find((page: any) => page.name === "Landing Page");
    const contentFields = heroContent?.fields || [];
    if (!contentFields || contentFields.length === 0) return '';

    const landingPageField = contentFields[1];
    const panelComponents = (landingPageField && 'components' in landingPageField)
      ? (landingPageField as { components?: any[] }).components || []
      : [];

    const landingPagePanel = panelComponents.find((comp: any) => comp.type === "panel" && comp.title?.includes("Landing Page Settings"));
    const landingPageComponents = landingPagePanel?.components || [];

    const backgroundField = landingPageComponents.find((field: any) => field.key === "Backgroundimage");

    if (backgroundField?.defaultValue && backgroundField.defaultValue.length > 0) {
      const backgroundData = backgroundField.defaultValue[0];
      if (backgroundData && backgroundData?.PageBackground && backgroundData.PageBackground.length > 0) {
        const PageBackground = backgroundData.PageBackground[0];
        const imgUrl = PageBackground.url
          ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${PageBackground.url}`
          : PageBackground.data?.url
            ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${PageBackground.data.url}`
            : null;
        return imgUrl || '';
      } else {
        const imgUrl = backgroundData.url
          ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${backgroundData.url}`
          : backgroundData.data?.url
            ? `${import.meta.env.VITE_APP_FOLDER_PATH}/${backgroundData.data.url}`
            : null;
        return imgUrl || '';
      }
    }

    return '';
  };

  // Helper: check if subheadline is enabled in config
  const isSubHeadlineEnabled = (() => {
    const landingPage = configData?.find((page: any) => page.name === "Landing Page");
    if (landingPage && Array.isArray(landingPage.configurations)) {
      const subHeadlineConfig = landingPage.configurations.find((cfg: any) => cfg.key === "sub headline");
      if (subHeadlineConfig && subHeadlineConfig.type === "switch") {
        return subHeadlineConfig.default !== false;
      }
    }
    return true;
  })();

  // Fetch sub-headline and background image from contentData
  useEffect(() => {
    if (contentData.length === 0) return;
    const fetchedBackgroundImage = getBackgroundImage();
    if (fetchedBackgroundImage) setBackgroundImage(fetchedBackgroundImage);
    // Subheadline is now handled by dynamicSubheadlines based on index
  }, [contentData]);

  return (
    <>
      <section
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('${backgroundImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid items-center gap-8 grid-cols-1 lg:grid-cols-2">
            {/* Left Side: Text Content */}
            <div className="text-white flex flex-col items-center justify-center">
              {/* Headline & Subheadline */}
              <div className="mb-5 text-center">
                <h1 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-4 scale-95'} px-2`}>
                  <span className="leading-tight">
                    {dynamicHeadlines[currentHeadlineIndex]}
                  </span>
                </h1>
                {/* Subheadline: show the one matching the current headline index if enabled */}
                {isSubHeadlineEnabled && dynamicSubheadlines.length > 0 && (
                  <p className={`text-lg sm:text-xl md:text-2xl mt-1 mb-4 sm:mb-6 opacity-90 px-4 font-medium transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-4 scale-95'}`}>
                    {dynamicSubheadlines[currentHeadlineIndex]}
                  </p>
                )}
              </div>

              {/* Headline indicators */}
              <div className="flex justify-center mb-8 px-2">
                {dynamicHeadlines.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 mx-1 rounded-full transition-all duration-500 cursor-pointer ${index === currentHeadlineIndex
                      ? 'bg-emerald-400 w-8 shadow-lg shadow-emerald-400/50'
                      : 'bg-white/40 hover:bg-white/60 w-2'
                      }`}
                    onClick={() => {
                      setIsVisible(false);
                      setTimeout(() => {
                        setCurrentHeadlineIndex(index);
                        setIsVisible(true);
                      }, 300);
                    }}
                  />
                ))}
              </div>

              {/* Action Buttons with conditional logic */}
              <div className="flex flex-col sm:flex-row gap-5 px-2 mt-5 justify-center">
                {/* Explore Button Logic */}
                {isExploreEnabled && (
                  <button
                    onClick={handleExplore}
                    className="h-12 sm:h-14 px-6 sm:px-8 btn-primary-gradient hover:btn-primary-gradient text-white font-semibold inline-flex items-center justify-center gap-2 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-emerald-500/40 whitespace-nowrap rounded-md ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                    data-testid="button-explore"
                  >
                    <Compass className="h-4 sm:h-5 w-4 sm:w-5" />
                    Explore
                  </button>
                )}

                {/* Create Free Account Button Logic: only show if not logged in */}
                {isLoginEnabled && !isLoggedIn && (
                  <Button
                    onClick={handleRegister}
                    size="lg"
                    className="h-12 sm:h-14 px-6 sm:px-8 bg-white text-gray-900 hover:bg-gradient-to-r hover:from-emerald-500 hover:to-teal-500 hover:text-white border-2 border-white hover:border-transparent font-semibold flex items-center justify-center gap-2 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-emerald-500/40"
                    data-testid="button-register-now"
                  >
                    <UserPlus className="h-4 sm:h-5 w-4 sm:w-5" />
                    Create Free Account
                  </Button>
                )}
              </div>
            </div>
            {/* Right Side: Dynamic Campaign Popup */}
              <div className="hidden lg:flex justify-center items-center pr-4 -mt-20">
                <div className="w-full max-w-md h-[550px]">
                  <DynamicCampaign 
                    isMobile={false}
                    showHeader={true}
                  />
                </div>
              </div>
          </div>
        </div>
      </section>
    </>
  );
}